import React, { useState, useEffect } from 'react';
import { RiskDonutChart } from '../components/RiskDonutChart';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Cell,
  Bar,
} from 'recharts';
import { 
  AlertTriangle, 
  Shield, 
  ShieldAlert, 
  ShieldCheck, 
  Info, 
  TrendingUp,
  Activity,
  Target,
  Database
} from 'lucide-react';
import styled, { keyframes } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import ScanHistorySection from '../components/HomeScanHistory';
import { apiRequest } from '../utils/api';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
`;

const DashboardContainer = styled.div`
  padding: 0.75rem;
  max-width: 100%;
  margin: 0 auto;
  background: #ffffff;
  min-height: 100vh;
  animation: ${fadeIn} 0.8s ease-out;
  overflow-x: hidden;
  
  @media (max-width: 768px) {
    padding: 0.5rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.25rem;
  }
`;

const HeaderSection = styled.div`
  margin-bottom: 1.25rem;
  animation: ${slideIn} 1s ease-out;
  padding: 0 0.25rem;
`;

const PageTitle = styled.h1`
  font-size: 1.5rem;
  font-weight: 700;
  background: linear-gradient(135deg, #1e293b 0%, #475569 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin: 0 0 0.125rem 0;
  line-height: 1.2;
  
  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.125rem;
  }
`;

const PageSubtitle = styled.p`
  font-size: 0.875rem;
  color: #64748b;
  font-weight: 500;
  margin: 0;
  line-height: 1.4;
  
  @media (max-width: 480px) {
    font-size: 0.8125rem;
  }
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 0.75rem;
  margin-bottom: 1.5rem;
  padding: 0 0.25rem;
  
  @media (max-width: 1024px) {
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
    gap: 0.625rem;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.5rem;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }
`;

const MetricCard = styled.div`
  background: ${(props) => {
    const baseColors = {
      Critical: 'linear-gradient(135deg, #7f1d1d 0%, #3e090a 100%)',
      High: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
      Medium: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
      Low: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
      Info: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
    };
    return baseColors[props.severity] || baseColors.Info;
  }};
  
  padding: 1rem;
  border-radius: 0.625rem;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  animation: ${fadeIn} 0.6s ease-out backwards;
  animation-delay: ${(props, index) => index * 0.1}s;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12);
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0.8) 50%, rgba(255,255,255,0.3) 100%);
  }
  
  @media (max-width: 768px) {
    padding: 0.875rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.75rem;
  }
`;

const MetricHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.875rem;
`;

const MetricTitle = styled.h3`
  color: rgba(255, 255, 255, 0.95);
  font-size: 0.8125rem;
  font-weight: 600;
  margin: 0;
  letter-spacing: 0.025em;
  line-height: 1.2;
  
  @media (max-width: 768px) {
    font-size: 0.75rem;
  }
`;

const MetricIcon = styled.div`
  width: 2rem;
  height: 2rem;
  border-radius: 0.5rem;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    width: 1rem;
    height: 1rem;
    color: rgba(255, 255, 255, 0.9);
  }
  
  @media (max-width: 768px) {
    width: 1.75rem;
    height: 1.75rem;
    
    svg {
      width: 0.875rem;
      height: 0.875rem;
    }
  }
`;

const MetricValue = styled.div`
  font-size: 1.75rem;
  font-weight: 700;
  color: white;
  line-height: 1;
  margin-bottom: 0.125rem;
  text-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.375rem;
  }
`;

const MetricTrend = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.6875rem;
  font-weight: 500;
  
  svg {
    width: 0.875rem;
    height: 0.875rem;
  }
  
  @media (max-width: 768px) {
    font-size: 0.625rem;
    
    svg {
      width: 0.75rem;
      height: 0.75rem;
    }
  }
`;

const ChartsSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.25rem;
  margin-bottom: 1.5rem;
  padding: 0 0.25rem;
  
  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
  
  @media (max-width: 768px) {
    gap: 0.875rem;
  }
`;

const ChartContainer = styled.div`
  background: #ffffff;
  border-radius: 0.75rem;
  padding: 1rem;
  border: 1px solid ${(props) => (props.theme === 'dark' 
    ? 'rgba(255, 255, 255, 0.1)' 
    : '#f1f5f9')};
  animation: ${fadeIn} 0.8s ease-out backwards;
  animation-delay: 0.3s;
  
  @media (max-width: 768px) {
    padding: 0.875rem;
    border-radius: 0.625rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.75rem;
  }
`;

const AnalyticsSection = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 1.25rem;
  margin-bottom: 1.5rem;
  padding: 0 0.25rem;
  
  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
  
  @media (max-width: 768px) {
    gap: 0.875rem;
  }
`;

const ChartTitle = styled.h3`
  font-size: 0.9375rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 0.875rem 0;
  display: flex;
  align-items: center;
  gap: 0.375rem;
  line-height: 1.2;
  
  svg {
    width: 0.875rem;
    height: 0.875rem;
    opacity: 0.7;
  }
  
  @media (max-width: 768px) {
    font-size: 0.875rem;
    margin-bottom: 0.75rem;
  }
`;

const ChartContent = styled.div`
  height: 220px;
  position: relative;
  
  @media (max-width: 768px) {
    height: 180px;
  }
  
  @media (max-width: 480px) {
    height: 160px;
  }
`;

const NoDataContainer = styled.div`
  text-align: center;
  padding: 1.5rem 1rem;
  background: #ffffff;
  border: 2px dashed #cbd5e1;
  border-radius: 0.75rem;
  animation: ${fadeIn} 0.8s ease-out;
  margin: 0 0.25rem;
  
  @media (max-width: 768px) {
    padding: 1.25rem 0.75rem;
  }
  
  @media (max-width: 480px) {
    padding: 1rem 0.5rem;
  }
`;

const WelcomeTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 0.5rem 0;
  line-height: 1.3;
  
  @media (max-width: 768px) {
    font-size: 1.125rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1rem;
  }
`;

const WelcomeText = styled.p`
  font-size: 0.875rem;
  color: #64748b;
  margin: 0 0 1.25rem 0;
  line-height: 1.5;
  
  @media (max-width: 768px) {
    font-size: 0.8125rem;
    margin-bottom: 1rem;
  }
  
  @media (max-width: 480px) {
    font-size: 0.75rem;
  }
`;

const CTAButton = styled.button`
  padding: 0.625rem 1.25rem;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  border: none;
  border-radius: 0.5rem;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 3px 12px rgba(59, 130, 246, 0.3);
  line-height: 1.2;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(59, 130, 246, 0.4);
    animation: ${pulse} 1.5s infinite;
  }
  
  @media (max-width: 768px) {
    padding: 0.5rem 1rem;
    font-size: 0.75rem;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.625rem;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }
`;

const StatCard = styled.div`
  background: #f8fafc;
  padding: 0.875rem;
  border-radius: 0.625rem;
  
  @media (max-width: 768px) {
    padding: 0.75rem;
  }
`;

const StatLabel = styled.div`
  font-size: 0.6875rem;
  font-weight: 500;
  color: #64748b;
  margin-bottom: 0.125rem;
  line-height: 1.2;
`;

const StatValue = styled.div`
  font-size: 1.125rem;
  font-weight: 600;
  color: #1e293b;
  line-height: 1.2;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const SEVERITY_COLORS = {
  Critical: '#3e090a',
  High: '#d32f2f',
  Medium: '#ffc324',
  Low: '#2e7d32',
  Info: '#3c99f4',
};

const SEVERITY_ICONS = {
  Critical: AlertTriangle,
  High: ShieldAlert,
  Medium: Shield,
  Low: ShieldCheck,
  Info: Info,
};

const HomePage = ({ theme }) => {
  const navigate = useNavigate();
  const [severityCounts, setSeverityCounts] = useState({
    Critical: 0,
    High: 0,
    Medium: 0,
    Low: 0,
    Info: 0,
  });
  const [loading, setLoading] = useState(true);
  const [trendData, setTrendData] = useState([]);

  useEffect(() => {
    const fetchSeverityData = async () => {
      try {
        const res = await apiRequest('/api/auth/overview', { method: 'GET' });
        if (!res.ok) {
          throw new Error(`Failed to fetch overview. Status ${res.status}`);
        }
        const data = await res.json();
        setSeverityCounts(data);
      } catch (err) {
        console.error('Error fetching severity data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchSeverityData();
  }, []);

  useEffect(() => {
    const fetchTrendData = async () => {
      const dataArray = [];
      const today = new Date();
      
      for (let i = 0; i < 7; i++) {
        const date = new Date(today);
        date.setDate(today.getDate() - i);
        const yyyy = date.getFullYear();
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const dd = String(date.getDate()).padStart(2, '0');
        const dateStr = `${yyyy}-${mm}-${dd}`;
        const startDate = `${dateStr}T00:00:00.000Z`;
        const endDate = `${dateStr}T23:59:59.999Z`;

        try {
          const res = await fetch(
            `https://services.nvd.nist.gov/rest/json/cves/2.0?pubStartDate=${startDate}&pubEndDate=${endDate}`
          );
          const json = await res.json();
          const counts = { 
            date: new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), 
            Critical: 0, 
            High: 0, 
            Medium: 0, 
            Low: 0,
            total: 0
          };
          
          if (json.vulnerabilities && Array.isArray(json.vulnerabilities)) {
            json.vulnerabilities.forEach((item) => {
              const baseSeverity = item.cve.metrics?.cvssMetricV31?.[0]?.cvssData?.baseSeverity;
              if (baseSeverity) {
                const severityKey = baseSeverity.charAt(0).toUpperCase() + baseSeverity.slice(1).toLowerCase();
                if (counts[severityKey] !== undefined) {
                  counts[severityKey] += 1;
                  counts.total += 1;
                }
              }
            });
          }
          dataArray.push(counts);
        } catch (error) {
          console.error(`Error fetching trend data for ${dateStr}:`, error);
          dataArray.push({ 
            date: new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), 
            Critical: 0, 
            High: 0, 
            Medium: 0, 
            Low: 0, 
            total: 0 
          });
        }
      }
      setTrendData(dataArray.reverse());
    };

    fetchTrendData();
  }, []);

  const totalSeverity = Object.values(severityCounts).reduce((a, b) => a + b, 0);

  if (loading) {
    return (
      <DashboardContainer>
        <HeaderSection>
          <PageTitle>Loading Security Dashboard...</PageTitle>
        </HeaderSection>
      </DashboardContainer>
    );
  }

  if (totalSeverity === 0) {
    return (
      <DashboardContainer>
        <NoDataContainer>
          <WelcomeTitle>Welcome to Your Security Dashboard</WelcomeTitle>
          <WelcomeText>
            It looks like you haven't performed any security scans yet. Start your first comprehensive 
            security assessment to monitor vulnerabilities and protect your digital assets.
          </WelcomeText>
          <CTAButton onClick={() => navigate('/autoscan')}>
            Launch Security Scan
          </CTAButton>
        </NoDataContainer>
      </DashboardContainer>
    );
  }

  return (
    <DashboardContainer>
      {/* <HeaderSection>
        <PageTitle>Security Dashboard</PageTitle>
        <PageSubtitle>Monitor and manage your security vulnerabilities</PageSubtitle>
      </HeaderSection> */}

      <MetricsGrid>
        {Object.entries(severityCounts).map(([severity, count], index) => {
          const IconComponent = SEVERITY_ICONS[severity];
          return (
            <MetricCard key={severity} severity={severity} style={{ animationDelay: `${index * 0.1}s` }}>
              <MetricHeader>
                <MetricTitle>{severity} Risk</MetricTitle>
                <MetricIcon>
                  <IconComponent />
                </MetricIcon>
              </MetricHeader>
              <MetricValue>{count}</MetricValue>
              <MetricTrend>
                <TrendingUp size={16} />
                <span>Active Vulnerabilities</span>
              </MetricTrend>
            </MetricCard>
          );
        })}
      </MetricsGrid>

      <AnalyticsSection>
        <ChartContainer>
          <ChartTitle>
            <Activity />
            Vulnerability Trends (7 Days)
          </ChartTitle>
          <ChartContent>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={trendData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="criticalGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#dc2626" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#dc2626" stopOpacity={0.05}/>
                  </linearGradient>
                  <linearGradient id="highGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#f59e0b" stopOpacity={0.05}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis 
                  dataKey="date" 
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#6b7280', fontSize: 11 }}
                />
                <YAxis 
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#6b7280', fontSize: 11 }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#ffffff',
                    border: 'none',
                    borderRadius: '6px',
                    boxShadow: '0 3px 10px rgba(0, 0, 0, 0.15)',
                    fontSize: '12px',
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="total"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  fill="url(#criticalGradient)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </ChartContent>
        </ChartContainer>

        <ChartContainer>
          <ChartTitle>
            <AlertTriangle />
            Severity Distribution
          </ChartTitle>
          <ChartContent>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={Object.entries(severityCounts).map(([name, value]) => ({ name, value }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis 
                  dataKey="name"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#6b7280', fontSize: 10 }}
                />
                <YAxis 
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#6b7280', fontSize: 11 }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#ffffff',
                    border: 'none',
                    borderRadius: '6px',
                    boxShadow: '0 3px 10px rgba(0, 0, 0, 0.15)',
                    fontSize: '12px',
                  }}
                />
                <Bar 
                  dataKey="value" 
                  radius={[4, 4, 0, 0]}
                >
                  {Object.entries(severityCounts).map(([name], index) => (
                    <Cell key={`cell-${index}`} fill={SEVERITY_COLORS[name]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartContent>
        </ChartContainer>
      </AnalyticsSection>

      <ScanHistorySection />
    </DashboardContainer>
  );
};

export default HomePage;